import { createReducer, on } from '@ngrx/store';
import { DocumentMedia } from '@src/app/core/types/document.type';
import { RemoteVideoMedia } from '@src/app/core/types/remote-video.type';
import { DocumentationActions } from './documentation.actions';
import { DocumentationTerms } from '@src/app/core/enum/documentation-terms.enum';
import { DocumentationSlugs } from '@src/app/core/enum/documentation-slugs.enum';
import { RemoteVideoSlugs } from '@src/app/core/enum/remote-video-slugs.enum';

export type DocumentFileUrl = { url: string | null; field_activado: boolean };
export interface DocumentationState {
  // Documentos organizados por términos
  documentsByTerm: {
    [key in DocumentationTerms]?: DocumentMedia[];
  };
  // Documentos organizados por slug (para acceso rápido)
  documentsBySlug: {
    [key in DocumentationSlugs]?: DocumentMedia;
  };
  // Estados de carga por término
  loadingByTerm: {
    [key in DocumentationTerms]?: boolean;
  };
  // Estados de carga por slug
  loadingBySlug: {
    [key in DocumentationSlugs]?: boolean;
  };
  // Errores por término
  errorsByTerm: {
    [key in DocumentationTerms]?: any;
  };
  // Errores por slug
  errorsBySlug: {
    [key in DocumentationSlugs]?: any;
  };
  // Vídeos remotos organizados por términos
  remoteVideosByTerm: {
    [key in DocumentationTerms]?: RemoteVideoMedia[];
  };
  // Vídeos remotos organizados por slug (para acceso rápido)
  remoteVideosBySlug: {
    [key in RemoteVideoSlugs]?: RemoteVideoMedia;
  };
  // Estados de carga de vídeos remotos por término
  loadingRemoteVideosByTerm: {
    [key in DocumentationTerms]?: boolean;
  };
  // Errores de vídeos remotos por término
  errorsRemoteVideosByTerm: {
    [key in DocumentationTerms]?: any;
  };
}

export const initialState: DocumentationState = {
  documentsByTerm: {},
  documentsBySlug: {},
  loadingByTerm: {},
  loadingBySlug: {},
  errorsByTerm: {},
  errorsBySlug: {},
  remoteVideosByTerm: {},
  remoteVideosBySlug: {},
  loadingRemoteVideosByTerm: {},
  errorsRemoteVideosByTerm: {},
};

export const documentationReducer = createReducer(
  initialState,

  // Cargar documentos por término
  on(DocumentationActions.loadDocumentsByTerm, (state, { term }) => ({
    ...state,
    loadingByTerm: {
      ...state.loadingByTerm,
      [term]: true,
    },
    errorsByTerm: {
      ...state.errorsByTerm,
      [term]: null,
    },
  })),

  on(
    DocumentationActions.loadDocumentsByTermSuccess,
    (state, { term, documents }) => {
      // Crear un objeto con los documentos indexados por slug
      const documentsBySlug = documents.reduce(
        (acc, doc) => {
          const slug = doc.attributes.field_slug as DocumentationSlugs;
          if (slug) {
            acc[slug] = doc;
          }
          return acc;
        },
        {} as { [key in DocumentationSlugs]?: DocumentMedia },
      );

      return {
        ...state,
        documentsByTerm: {
          ...state.documentsByTerm,
          [term]: documents,
        },
        // Actualizar también documentsBySlug con los documentos cargados
        documentsBySlug: {
          ...state.documentsBySlug,
          ...documentsBySlug,
        },
        loadingByTerm: {
          ...state.loadingByTerm,
          [term]: false,
        },
      };
    },
  ),

  on(
    DocumentationActions.loadDocumentsByTermFailure,
    (state, { term, error }) => ({
      ...state,
      loadingByTerm: {
        ...state.loadingByTerm,
        [term]: false,
      },
      errorsByTerm: {
        ...state.errorsByTerm,
        [term]: error,
      },
    }),
  ),

  // Cargar documento por slug
  on(DocumentationActions.loadDocumentBySlug, (state, { slug }) => ({
    ...state,
    loadingBySlug: {
      ...state.loadingBySlug,
      [slug]: true,
    },
    errorsBySlug: {
      ...state.errorsBySlug,
      [slug]: null,
    },
  })),

  on(
    DocumentationActions.loadDocumentBySlugSuccess,
    (state, { slug, document }) => ({
      ...state,
      documentsBySlug: {
        ...state.documentsBySlug,
        [slug]: document,
      },
      loadingBySlug: {
        ...state.loadingBySlug,
        [slug]: false,
      },
    }),
  ),

  on(
    DocumentationActions.loadDocumentBySlugFailure,
    (state, { slug, error }) => ({
      ...state,
      loadingBySlug: {
        ...state.loadingBySlug,
        [slug]: false,
      },
      errorsBySlug: {
        ...state.errorsBySlug,
        [slug]: error,
      },
    }),
  ),

  // Cargar vídeos remotos por término
  on(DocumentationActions.loadRemoteVideosByTerm, (state, { term }) => ({
    ...state,
    loadingRemoteVideosByTerm: {
      ...state.loadingRemoteVideosByTerm,
      [term]: true,
    },
    errorsRemoteVideosByTerm: {
      ...state.errorsRemoteVideosByTerm,
      [term]: null,
    },
  })),

  on(
    DocumentationActions.loadRemoteVideosByTermSuccess,
    (state, { term, videos }) => {
      // Indexar vídeos remotos por slug
      const remoteVideosBySlug = videos.reduce(
        (acc, video) => {
          const slug = video.attributes.field_slug as RemoteVideoSlugs;
          if (slug) {
            acc[slug] = video;
          }
          return acc;
        },
        {} as { [key in RemoteVideoSlugs]?: RemoteVideoMedia },
      );

      return {
        ...state,
        remoteVideosByTerm: {
          ...state.remoteVideosByTerm,
          [term]: videos,
        },
        remoteVideosBySlug: {
          ...state.remoteVideosBySlug,
          ...remoteVideosBySlug,
        },
        loadingRemoteVideosByTerm: {
          ...state.loadingRemoteVideosByTerm,
          [term]: false,
        },
      };
    },
  ),

  on(
    DocumentationActions.loadRemoteVideosByTermFailure,
    (state, { term, error }) => ({
      ...state,
      loadingRemoteVideosByTerm: {
        ...state.loadingRemoteVideosByTerm,
        [term]: false,
      },
      errorsRemoteVideosByTerm: {
        ...state.errorsRemoteVideosByTerm,
        [term]: error,
      },
    }),
  ),

  // Limpiar estado
  on(DocumentationActions.clearDocumentation, () => initialState),
);
